import {
  Truck,
  Receipt,
  ReceiptText,
  Undo2,
  Landmark,
  Wallet,
  BookText,
  Link as LinkIcon,
  Clock,
  AlertTriangle,
  CircleX,
  CircleSlash,
  CircleDashed,
  Unlink,
  ShieldAlert,
  type LucideIcon,
} from 'lucide-react';
import { DocLinkStatus } from '../types/documentCapture.types';
import { PERMISSIONS } from '@features/shared/constants/permissions';

/**
 * Document Capture identity config (86d3rrf3e), ported from `Scanning Service Feature.dc.html`
 * (`FAM()` / `LINK()`), which is the source of truth for labels, hues and tooltip copy.
 *
 * A SEVENTH family must be a data change here plus a registry row server-side — never a redesign.
 */

export type DocTier = 'sales' | 'treasury';

export interface FamilyConfig {
  /** Series prefix, e.g. "DR". */
  prefix: string;
  label: string;
  icon: LucideIcon;
  /**
   * Fixed categorical identity hue as raw HSL channels, applied via a `--fam` custom property so
   * the same value drives text/border/background at different alphas in all three themes.
   *
   * Deliberate exception to the semantic-token rule: these are categorical identity colours (the
   * same carve-out the design system makes for data-viz series), and accounting recognises the
   * families by colour. See UI-GUIDELINES §3.
   */
  hue: string;
  tier: DocTier;
  /** The per-family view permission that gates this family everywhere. */
  permission: string;
  /**
   * Whether an opener WITHOUT this family's view permission still gets the minimal customer view
   * (86d3uu104, T10 — mirrors `DocPrefixConfig.PublicShareEnabled`). All six registered families
   * since 86d3yek7a; a seventh starts false, which is what keeps the staff-only wording alive.
   *
   * ⚠️ Meaning changed in T10 and the flag survived. It used to mean "can be shared at all" and hid
   * the share UI; now EVERY family has a link (it's what NetSuite points at) and this only decides
   * what an unpermitted opener sees. Use it to pick the copy-time wording — never to hide Copy.
   *
   * ⚠️ FE only, and the server no longer holds a second opinion. Until 86d3yek7a the share guard
   * refused the treasury tier on top of the flag, so a wrong value here was still caught server-side
   * for OP/DV. That refusal is gone: authority is now ONE boolean per row in `DocPrefixConfigs`, and
   * this table must mirror it. The 86d3v5k47 lesson is the cost of drift — while the two disagreed,
   * staff read "Staff-only link" about documents customers could open.
   */
  shareable?: boolean;
}

export const FAMILIES: Record<string, FamilyConfig> = {
  DR: {
    prefix: 'DR',
    label: 'Delivery Receipt',
    icon: Truck,
    hue: '217 91% 55%',
    tier: 'sales',
    permission: PERMISSIONS.DOCSCAN_VIEW_DR,
    shareable: true,
  },
  INV: {
    prefix: 'INV',
    label: 'Sales Invoice',
    icon: Receipt,
    hue: '160 84% 39%',
    tier: 'sales',
    permission: PERMISSIONS.DOCSCAN_VIEW_INV,
    shareable: true,
  },
  INT: {
    prefix: 'INT',
    label: 'Sales Invoice – NT',
    icon: ReceiptText,
    hue: '190 88% 42%',
    tier: 'sales',
    permission: PERMISSIONS.DOCSCAN_VIEW_INT,
    // 86d3v5k47 — must match DocPrefixConfigs.PublicShareEnabled for INT. The server is the
    // authority; this drives the wording a staffer reads at the moment of copying, and while it
    // was absent that wording said "Staff-only link" about a document customers are meant to open.
    shareable: true,
  },
  CM: {
    prefix: 'CM',
    label: 'Credit Memo',
    icon: Undo2,
    hue: '25 95% 53%',
    tier: 'sales',
    permission: PERMISSIONS.DOCSCAN_VIEW_CM,
    // 86d3yek7a — same sales tier and the same customer as INV/INT; being staff-only was an omission,
    // not a decision. Mirrors DocPrefixConfigs.PublicShareEnabled, flipped by the same commit.
    shareable: true,
  },
  OP: {
    prefix: 'OP',
    label: 'Outright Payment',
    icon: Landmark,
    hue: '250 60% 62%',
    tier: 'treasury',
    permission: PERMISSIONS.DOCSCAN_VIEW_OP,
    // 86d3yek7a, owner decision 2026-08-06 — treasury is public too, and the server's tier refusal is
    // gone with it. This is now genuinely load-bearing: it is the wording a staffer reads before
    // sending a payment voucher to someone outside the company, so it must not say "safe to send"
    // unless the registry row agrees. Anonymous opens are audited per-visit (IP/UA, no user).
    shareable: true,
  },
  DV: {
    prefix: 'DV',
    label: 'Disbursement Voucher',
    icon: Wallet,
    hue: '270 52% 60%',
    tier: 'treasury',
    permission: PERMISSIONS.DOCSCAN_VIEW_DV,
    // 86d3yek7a — see OP above.
    shareable: true,
  },
  JV: {
    prefix: 'JV',
    label: 'Journal Voucher',
    icon: BookText,
    // Rose, chosen for the widest free arc on the wheel. The six in use are 217/160/190/25/250/270;
    // sorted, the only gaps wider than ~35° are 25→160 (midpoint lands in yellow-green, which reads
    // badly as text) and 270→25, whose midpoint is this. 340 sits 70° off DV's violet and 45° off
    // CM's orange — comparable separation to pairs already shipping.
    hue: '340 75% 55%',
    tier: 'treasury',
    permission: PERMISSIONS.DOCSCAN_VIEW_JV,
    // 14ykpqtpupa, owner decision 2026-09-21 — public, consistent with 86d3yek7a.
    // ⚠️ MUST mirror DocPrefixConfigs.PublicShareEnabled for JV. The server is the authority; this
    // only picks the wording a staffer reads at the moment of copying. See the INT drift at :55-59.
    shareable: true,
  },
};

export const FAMILY_ORDER = ['DR', 'INV', 'INT', 'CM', 'OP', 'DV', 'JV'] as const;

export const TREASURY_PREFIXES = FAMILY_ORDER.filter((p) => FAMILIES[p].tier === 'treasury');

/** Family for a document number or prefix. Returns undefined for an unregistered series. */
export function familyOf(numberOrPrefix: string | null | undefined): FamilyConfig | undefined {
  if (!numberOrPrefix) return undefined;
  return FAMILIES[numberOrPrefix.split('-')[0].toUpperCase()];
}

// ---------------------------------------------------------------- link status

export type LinkTone = 'success' | 'info' | 'warning' | 'destructive' | 'muted';

export interface LinkConfig {
  label: string;
  tone: LinkTone;
  icon: LucideIcon;
  /** Subtle pulse for the self-resolving state. Suppressed under prefers-reduced-motion. */
  pulse?: boolean;
  /** Verbatim tooltip — this copy is the entire user education for link status. */
  tip: string;
}

export const LINK_STATUS: Record<DocLinkStatus, LinkConfig> = {
  [DocLinkStatus.Pending]: {
    label: 'Queued',
    tone: 'muted',
    icon: CircleDashed,
    tip: 'Captured and queued — DOP is about to look this number up in NetSuite.',
  },
  [DocLinkStatus.AwaitingNs]: {
    label: 'Waiting for NetSuite',
    tone: 'info',
    icon: Clock,
    pulse: true,
    // Ported from the prototype MINUS its "keeps retrying for 7 days" promise: the multi-day
    // retry ladder was removed (86d3rrfbf decision 4) once we confirmed the NetSuite record
    // always exists before its paper, so this is now a seconds-to-minutes transient. The
    // "expected, not an error" reassurance is the part that must survive.
    tip: 'Captured, but the number isn’t in NetSuite yet. DOP keeps checking and links it automatically — this is expected, not an error.',
  },
  [DocLinkStatus.Linked]: {
    label: 'Linked',
    tone: 'success',
    icon: LinkIcon,
    tip: 'The scan is attached to its NetSuite transaction — anyone in the ERP can open it.',
  },
  [DocLinkStatus.LinkedVoid]: {
    label: 'Linked (voided)',
    tone: 'muted',
    icon: CircleSlash,
    tip: 'Attached, but the NetSuite transaction itself is voided — kept for the record trail.',
  },
  [DocLinkStatus.SkippedClosedPeriod]: {
    label: 'Closed period',
    tone: 'warning',
    icon: AlertTriangle,
    tip: 'The accounting period is closed, so NetSuite refused the update. An admin can reopen it or allow non-G/L changes.',
  },
  [DocLinkStatus.Conflict]: {
    label: 'Conflict',
    tone: 'warning',
    icon: AlertTriangle,
    tip: 'NetSuite’s attachment field already holds an unknown value. A reviewer decides what to keep.',
  },
  [DocLinkStatus.Failed]: {
    label: 'Link failed',
    tone: 'destructive',
    icon: CircleX,
    tip: 'NetSuite rejected the update. Surfaced here so it’s never silent — retry after fixing.',
  },
  [DocLinkStatus.LinkBroken]: {
    label: 'Link removed',
    tone: 'muted',
    icon: Unlink,
    tip: 'The scan was withdrawn and its NetSuite attachment cleared. The document is kept for audit.',
  },
  // 86d45ffw4 — WARNING, not success, and the wording carries the same load. The design mock painted
  // this green, but green here would mean "NetSuite holds the link" on the strength of a person
  // saying so, and a document nobody actually pasted would look identical to a verified one. The
  // state is unconfirmed BY CONSTRUCTION — the sweep promotes it to a plain Linked as soon as it sees
  // the token on the record — so anything still wearing this chip is a claim nobody has checked.
  [DocLinkStatus.ManuallyLinked]: {
    label: 'Manually linked',
    tone: 'warning',
    icon: LinkIcon,
    tip: 'Someone pasted the scan link onto the NetSuite record by hand and said so here. DOP has not seen it on the record yet — it checks nightly, and switches this to a plain Linked once it does.',
  },
  // 14ykpqtwauu — RETIRED 14ykpqtx1z9 (2026-10-26): journal links now go through CSC IT's link service,
  // which leaves credit applications alone, so DOP no longer withholds anything. The entry STAYS: a
  // document can carry this status until the retirement sweep re-arms it, and history rows keep it.
  [DocLinkStatus.LinkWithheld]: {
    label: 'Link withheld',
    tone: 'warning',
    icon: ShieldAlert,
    tip: 'Held under the old applied-credits rule. DOP now links journals through NetSuite’s link service, which leaves credit applications alone — the nightly sweep re-queues this link on its own.',
  },
};

/** Tailwind classes per tone — semantic tokens, so all three themes are covered. */
export const LINK_TONE_CLASSES: Record<LinkTone, string> = {
  success: 'bg-success/12 text-success border-success/25',
  info: 'bg-info/12 text-info border-info/25',
  warning: 'bg-warning/12 text-warning border-warning/25',
  destructive: 'bg-destructive/12 text-destructive border-destructive/25',
  muted: 'bg-muted text-muted-foreground border-border',
};

// ---------------------------------------------------------------- capability links (T10, 86d3uu104)

/**
 * Make a link absolute. The server builds it from `ViewerBaseUrl`, which may be unset outside
 * production — the URL is then a bare `/p/{token}` that means nothing once it leaves the browser.
 *
 * `origin` is injectable so this stays a pure function under test (the suite runs without a DOM).
 */
export function absoluteShareUrl(
  url: string | null | undefined,
  origin: string = typeof window === 'undefined' ? '' : window.location.origin
): string | null {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * Who can actually open this family's link — said at the moment of copy, which is the only moment
 * the staffer is thinking about it.
 *
 * Every family has a link and Copy is offered on all of them. Since 86d3yek7a all six REGISTERED
 * families render for an opener without the view permission, so in practice every copy today reads
 * "safe to send". The staff-only branch is deliberately kept, not collapsed: it is the live fallback
 * for a family this table doesn't know (a seventh series is a data change server-side and reaches the
 * UI before anyone adds it here) and for a family deliberately left unpublished. Collapsing it would
 * mean the one case that most needs a warning is the one case that gets a reassurance.
 */
export function copyAudienceHint(prefix: string): string {
  const family = FAMILIES[prefix];
  return family?.shareable
    ? 'Safe to send to the customer — they can view and download it without signing in.'
    : `Staff-only link — anyone without ${family?.label ?? 'this document type'} permission sees “unavailable”.`;
}
